import { colors } from '@globals/themes/defaultTheme'
import {
  ListTenantMembersParams,
  MediaVariantEnum,
  PageResult,
  TenantMember,
  listTenantMembers,
} from '@kudos/http-client'
import { useRollbar } from '@rollbar/react'
import { fullNameFromParts } from '@shared/utils/nameFormatting'
import { useT } from '@transifex/react'
import { useEffect, useRef } from 'react'
import Tribute, { TributeItem } from 'tributejs'
import { useDebouncedCallback } from 'use-debounce'
import type { RichTextEditor } from './RichTextEditorField'

// Froala's keycode for Enter. Inlined so this hook doesn't statically import
// `froala-editor` (which would pull the editor into the initial bundle); the
// value is a stable DOM keycode.
const ENTER_KEYCODE = 13

const MAX_MENTIONS_PER_PAGE = 5
const AVATAR_SIZE = 24 // Size for avatar in mentions menu
const DEBOUNCE_DELAY = 300 // Debounce delay in milliseconds for user name search

export type MentionItem = {
  key: string
  value: string
  avatarUrl?: string | null
}

// Minimal types for Tribute internals used in the positioning patch
type TributeRange = { positionMenuAtCaret?: (scrollTo: boolean) => void }
type TributeWithRange = Tribute<MentionItem> & { range?: TributeRange }

/**
 * Returns the HTML string for an @-mention chip. The `contenteditable="false"`
 * attribute makes the span atomic inside Froala's contenteditable element: the
 * browser cannot place the caret inside it, so any text typed near the chip
 * lands outside it in the normal text flow.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/contenteditable
 */
export const getMentionSpanHtml = (item: MentionItem): string =>
  `<span class="fr-deletable fr-tribute" contenteditable="false" data-id="${item.value}" data-type="mention">${item.key}</span>`

/**
 * Builds the HTML inserted when a mention is chosen from the menu.
 *
 * Tribute calls the collection's `selectTemplate` with `undefined` when the
 * user hits Enter/Tab (or clicks) while the "No results found" menu is
 * showing: its select handlers only guard on `filteredItems` being truthy, and
 * an empty array is truthy, so it still calls `selectItemAtIndex(0)` with
 * `filteredItems[0] === undefined`. Returning `null` makes Tribute insert
 * nothing (`selectItemAtIndex` skips `replaceText` when the template returns
 * null) instead of crashing on `item.original` (Rollbar / SYN-7679).
 */
export const buildMentionSelectHtml = (item?: TributeItem<MentionItem>): string | null =>
  item ? getMentionSpanHtml(item.original) : null

/**
 * Builds the menu contents shown when a search returns no results. The message
 * must be wrapped in an `<li>`: Tribute's menu click handler walks up from the
 * click target until it finds a list item, throwing "cannot find the <li>
 * container for the click" if it reaches the menu root first. Returning bare
 * text caused that crash when the user clicked the "No results found" row
 * (Rollbar / SYN-7679). The `<li>` has no `data-index`, so `selectItemAtIndex`
 * bails out via its `isNaN` guard rather than selecting anything.
 *
 * The `no-match` class is Tribute's own convention for a non-selectable row and
 * carries `cursor: default` (from `tributejs/dist/tribute.css`), so the row
 * doesn't show the pointer cursor used for real, clickable mentions.
 */
export const buildMentionNoMatchHtml = (label: string): string =>
  `<li class="no-match">${label}</li>`

export const normalizeMentionSpans = (el: HTMLElement): void => {
  el.querySelectorAll<HTMLElement>('span[data-type="mention"]').forEach(mentionSpan => {
    mentionSpan.contentEditable = 'false'
  })
}

/**
 * Replaces Tribute's positionMenuAtCaret with a version that uses
 * `position: fixed` + viewport coordinates from a temporary marker element.
 *
 * Tribute's built-in positioning uses `position: absolute` and adds
 * `pageYOffset` to convert viewport coords to page-absolute ones. This breaks
 * when any ancestor of `<body>` introduces a new containing block (e.g. via
 * CSS `transform`, `will-change`, or `filter`). `position: fixed` is immune
 * to this because it always positions relative to the viewport — exactly what
 * `getBoundingClientRect()` returns.
 *
 * On mobile, two additional issues are addressed:
 *  1. Collapsed-range rects from `getBoundingClientRect()` frequently return
 *     all-zeros on mobile Safari/Android. A forced reflow (`offsetHeight`)
 *     after inserting the marker span ensures the browser computes layout
 *     before we read the rect. If the rect is still zero, we fall back to the
 *     editor element's own bounding rect so the menu appears near the editor
 *     rather than at (0, 0).
 *  2. When the virtual keyboard is open, the visual viewport can be offset
 *     from the layout viewport. `position: fixed` uses the layout viewport,
 *     while `getBoundingClientRect()` returns visual-viewport coordinates.
 *     We bridge the gap with `window.visualViewport.offsetTop/offsetLeft` and
 *     use visual-viewport dimensions for overflow checks.
 */
function patchTributePositioning(tribute: Tribute<MentionItem>) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tributeRange = (tribute as any).range

  tributeRange.positionMenuAtCaret = function (
    this: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    scrollTo: boolean,
  ) {
    const info = this.getTriggerInfo(
      false,
      this.tribute.hasTrailingSpace,
      true,
      this.tribute.allowSpaces,
      this.tribute.autocompleteMode,
    )

    if (typeof info === 'undefined') {
      this.tribute.menu.style.cssText = 'display: none'
      return
    }

    if (!this.tribute.positionMenu) {
      this.tribute.menu.style.cssText = 'display: block;'
      return
    }

    // For inputs / textareas fall back to the original textarea helper
    if (!this.isContentEditable(this.tribute.current.element)) {
      const coords = this.getTextAreaOrInputUnderlinePosition(
        this.tribute.current.element,
        info.mentionPosition,
      )
      this.tribute.menu.style.cssText = `top: ${coords.top}px; left: ${coords.left}px; position: absolute; display: block;`
      return
    }

    // --- contenteditable path: marker-based fixed positioning ---
    const sel = this.getWindowSelection()
    if (!sel?.anchorNode) {
      this.tribute.menu.style.cssText = 'display: none'
      return
    }

    const doc = this.getDocument() as Document
    const range = doc.createRange()
    try {
      range.setStart(sel.anchorNode, info.mentionPosition)
      range.collapse(true)
    } catch {
      // range.setStart can throw if anchorNode is detached or mentionPosition is
      // out of bounds — hide the menu and bail out without crashing.
      this.tribute.menu.style.cssText = 'display: none'
      return
    }

    // Try collapsed-range rect first; fall back to a temp marker if it's zero.
    // Mobile browsers frequently return all-zero rects for collapsed ranges.
    let rect = range.getBoundingClientRect()
    let marker: HTMLSpanElement | null = null

    if (!rect.height && !rect.top && !rect.left) {
      const m = doc.createElement('span')
      m.textContent = '\u200b'
      range.insertNode(m)
      void m.offsetHeight // force reflow so mobile browsers compute layout
      rect = m.getBoundingClientRect()
      marker = m
    }

    // Final fallback: if the rect is still zero (can happen on mobile when the
    // editor is inside a scrollable drawer and the browser hasn't flushed layout),
    // position relative to the editor element itself.
    if (!rect.height && !rect.top && !rect.left) {
      const editorElement = this.tribute.current.element as HTMLElement | null
      if (editorElement) {
        const editorRect = editorElement.getBoundingClientRect()
        rect = new DOMRect(editorRect.left, editorRect.top, 0, 0)
      }
    }

    // Account for visual-viewport offset. On mobile, the virtual keyboard can
    // shift the visual viewport within the layout viewport. position:fixed uses
    // layout-viewport coords, but getBoundingClientRect() returns visual-viewport
    // coords, so we bridge the gap.
    const vv = window.visualViewport
    const vvOffsetTop = vv?.offsetTop ?? 0
    const vvOffsetLeft = vv?.offsetLeft ?? 0

    const top = rect.bottom + vvOffsetTop
    const left = rect.left + vvOffsetLeft

    // Render the menu offscreen first so we can measure its intrinsic
    // dimensions without the browser constraining its layout to the
    // remaining space near a viewport edge.
    this.tribute.menu.style.cssText =
      'top: -9999px; left: -9999px; position: fixed; display: block;'

    const menuW = this.tribute.menu.offsetWidth
    const menuH = this.tribute.menu.offsetHeight

    if (marker) {
      marker.remove()
      sel.anchorNode?.parentNode?.normalize?.()
    }

    const viewportW = vv?.width ?? window.innerWidth
    const viewportH = vv?.height ?? window.innerHeight
    const edgePad = 8

    let finalLeft = left
    let finalTop = top

    if (left + menuW + edgePad > vvOffsetLeft + viewportW) {
      finalLeft = Math.max(vvOffsetLeft + edgePad, vvOffsetLeft + viewportW - menuW - edgePad)
    }

    if (top + menuH + edgePad > vvOffsetTop + viewportH) {
      finalTop = Math.max(vvOffsetTop, rect.top + vvOffsetTop - menuH)
    }

    this.tribute.menu.style.cssText = `top: ${finalTop}px; left: ${finalLeft}px; position: fixed; display: block;`

    if (scrollTo) this.scrollIntoView()
  }
}

export const useFroalaMentions = (
  editorInstance: RichTextEditor | null,
  tenantMemberId: string | undefined,
  isEnabled: boolean,
) => {
  const t = useT()
  const rollbar = useRollbar()
  const tributeRef = useRef<Tribute<MentionItem> | null>(null)
  const styleRef = useRef<HTMLStyleElement | null>(null)
  const isFetchingRef = useRef<boolean>(false)

  const repositionMenuIfActive = () => {
    // Reposition menu after results change to prevent losing anchor near viewport bottom
    const tribute = tributeRef.current as TributeWithRange | null
    if (tribute?.isActive) {
      tribute.range?.positionMenuAtCaret?.(false)
    }
  }

  // Inject mentions-specific CSS styles
  useEffect(() => {
    if (!isEnabled) {
      return
    }

    // Create and inject style element for mentions
    const styleId = 'froala-mentions-styles'
    let styleElement = document.getElementById(styleId) as HTMLStyleElement | null

    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = styleId
      styleElement.textContent = `
        .fr-wrapper span[data-type="mention"] {
          color: ${colors.bluePrimary};
          font-weight: 600;
          gap: 4px;
        }
        /* Tribute.js mentions menu overrides */
        .tribute-container {
          box-shadow: rgba(90, 91, 102, 0.15) 0px 2px 8px 0px;
          border-radius: 16px;
          border-color: ${colors.grayExtraLight};
          border-width: 1px;
          border-style: solid;
        }
        .tribute-container ul {
          padding: 8px;
          background-color: ${colors.white} !important;
        }
        .tribute-container li {
          padding: 6px 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          margin-right: -8px;
          margin-left: -8px;
        }
        .tribute-container li.highlight {
          /* Style the highlighted/selected menu item */
          background-color: ${colors.blueExtraLight};
        }
        .tribute-menu-item {
          font-size: 1rem;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .tribute-menu-item-avatar {
          width: ${AVATAR_SIZE}px;
          height: ${AVATAR_SIZE}px;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
        }
      `
      document.head.appendChild(styleElement)
      styleRef.current = styleElement
    }

    return () => {
      // Cleanup: remove style element when mentions are disabled
      if (styleRef.current && styleRef.current.parentNode) {
        styleRef.current.parentNode.removeChild(styleRef.current)
        styleRef.current = null
      }
    }
  }, [isEnabled])

  // Debounced function to perform the API call
  const debouncedSearchMembers = useDebouncedCallback(
    (searchText: string, callback: (items: MentionItem[]) => void, memberId: string) => {
      if (!memberId) {
        isFetchingRef.current = false
        callback([])
        return
      }

      isFetchingRef.current = true

      const params: ListTenantMembersParams = {
        notIdIn: [memberId],
        suspended: false,
        page: 1,
        pageSize: MAX_MENTIONS_PER_PAGE,
        embeds: ['avatar'],
      }

      if (searchText) {
        params.search = searchText
      }

      listTenantMembers(params)
        .then((response: PageResult<TenantMember>) => {
          const items: MentionItem[] = (response?.results || []).map((member: TenantMember) => {
            // Extract avatar URL - prefer AVATAR_SM variant, fallback to base URL
            let avatarUrl: string | null = null
            if (member.avatarUrls) {
              const avatarVariant = member.avatarUrls.variants?.find(
                v => v.variant === MediaVariantEnum.AVATAR_SM,
              )
              avatarUrl = avatarVariant?.url ?? member.avatarUrls.url ?? null
            }

            return {
              key: fullNameFromParts(member.firstName, member.lastName),
              value: member.id,
              avatarUrl,
            }
          })
          isFetchingRef.current = false
          callback(items)
          repositionMenuIfActive()
        })
        .catch((error: unknown) => {
          rollbar.error(error instanceof Error ? error : String(error))
          isFetchingRef.current = false
          callback([])
          repositionMenuIfActive()
        })
    },
    DEBOUNCE_DELAY,
  )

  useEffect(() => {
    if (!isEnabled || !editorInstance || !tenantMemberId) {
      return
    }

    // Access the editor element directly (editor.el is the Froala editor element)
    const editor = editorInstance as unknown as {
      events: {
        on: (
          event: string,
          handler: (e: { which: number }) => boolean | void,
          capture: boolean,
        ) => void
      }
      el?: HTMLElement
    }

    if (!editor.el) {
      return
    }

    const editorElement = editor.el

    // Create Tribute instance with async lookup using listTenantMembers
    const tribute = new Tribute<MentionItem>({
      // We allow spaces so that if there are users with the same first name,
      // you can narrow down on the last name.
      allowSpaces: true,
      requireLeadingSpace: false,

      // listTenantMembers already filters. Tribute's local fuzzy filter would
      // drop matches when mentionText ends with a trailing space (e.g. user
      // typed "@John Smith" then deleted the last name, leaving "@John ").

      // Note that pre and post values are the defaults for tribute js however
      // we get a ts error if we omit them!
      searchOpts: { skip: true, pre: '<span>', post: '</span>' },
      values: function (text: string, callback: (items: MentionItem[]) => void) {
        const searchText = text.trim()

        // Set fetching flag immediately to prevent "No match found" from showing
        // while the debounced search is pending
        isFetchingRef.current = true

        // Don't call callback([]) immediately - wait for debounced search results
        // This prevents "No match found" from appearing while results are loading

        // Trigger debounced search
        debouncedSearchMembers(searchText, callback, tenantMemberId)
      },
      menuItemTemplate: function (item) {
        const avatarHtml = item.original.avatarUrl
          ? `<img src="${item.original.avatarUrl}" alt="" class="tribute-menu-item-avatar" />`
          : `<div class="tribute-menu-item-avatar" style="background-color: ${colors.blueUltraLight}; display: flex; align-items: center; justify-content: center; color: ${colors.blueLight}; font-size: ${Math.round(AVATAR_SIZE / 2)}px; font-weight: 600;">${(item.original.key.charAt(0) || '').toUpperCase()}</div>`
        return `<div class="tribute-menu-item">${avatarHtml}<span>${item.original.key}</span></div>`
      },
      // Cast: Tribute's typings declare the return as `string`, but its runtime
      // treats a `null` return as "insert nothing", which is how we avoid a
      // crash when the user selects with no results (see buildMentionSelectHtml).
      selectTemplate: (item => buildMentionSelectHtml(item)) as (
        item: TributeItem<MentionItem>,
      ) => string,
      noMatchTemplate: function () {
        return buildMentionNoMatchHtml(t('No results found'))
      },
    })

    patchTributePositioning(tribute)

    // Attach Tribute to editor element
    tribute.attach(editorElement)
    normalizeMentionSpans(editorElement)
    tributeRef.current = tribute

    // Guard against mention spans injected after init (e.g. draft edit via html.set).
    const mentionObserver = new MutationObserver(() => {
      normalizeMentionSpans(editorElement)
    })
    mentionObserver.observe(editorElement, { childList: true, subtree: true })

    editor.events.on(
      'keydown',
      function (e: { which: number }) {
        if (e.which === ENTER_KEYCODE && tribute.isActive) {
          return false
        }
      },
      true,
    )

    // Keep the mentions menu anchored to the "@" while any ancestor scrolls
    // or the visual viewport changes (mobile keyboard open/close, zoom).
    // This uses a lightweight repositioning path that reads the caret rect
    // directly — no marker insertion, no trigger re-detection — so it's
    // safe to run on every scroll frame without jitter.
    let scrollRafId: number | null = null
    let lastFlippedAbove = false

    const resetFlipState = () => {
      lastFlippedAbove = false
    }
    editor.el.addEventListener('tribute-active-true', resetFlipState)

    const repositionMenuOnScroll = () => {
      scrollRafId = null
      if (!tribute.isActive) return

      try {
        const sel = window.getSelection()
        if (!sel?.rangeCount) return

        const rect = sel.getRangeAt(0).getBoundingClientRect()
        if (!rect.height && !rect.top && !rect.left) return

        const vv = window.visualViewport
        const vvTop = vv?.offsetTop ?? 0
        const vvLeft = vv?.offsetLeft ?? 0
        const viewportW = vv?.width ?? window.innerWidth
        const viewportH = vv?.height ?? window.innerHeight

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const menu = (tribute as any).menu as HTMLElement
        const menuW = menu.offsetWidth
        const menuH = menu.offsetHeight

        let top = rect.bottom + vvTop
        let left = rect.left + vvLeft

        const edgePad = 8
        if (left + menuW + edgePad > vvLeft + viewportW) {
          left = Math.max(vvLeft + edgePad, vvLeft + viewportW - menuW - edgePad)
        }

        // Hysteresis: once flipped above, stay above until there's comfortable
        // room below (20 px buffer) to prevent oscillation near the edge.
        const fitsBelow = top + menuH + edgePad <= vvTop + viewportH
        const comfortablyFitsBelow = top + menuH + 20 <= vvTop + viewportH

        if (lastFlippedAbove && !comfortablyFitsBelow) {
          top = Math.max(vvTop, rect.top + vvTop - menuH)
        } else if (!fitsBelow) {
          top = Math.max(vvTop, rect.top + vvTop - menuH)
          lastFlippedAbove = true
        } else {
          lastFlippedAbove = false
        }

        menu.style.top = `${top}px`
        menu.style.left = `${left}px`
      } catch {
        // Selection may have been lost during scroll — ignore silently
      }
    }

    const handleScrollOrResize = () => {
      if (!tribute.isActive) return
      if (scrollRafId !== null) return
      scrollRafId = requestAnimationFrame(repositionMenuOnScroll)
    }

    // scroll doesn't bubble, so capture: true is needed to intercept scroll
    // events from any ancestor container (e.g. a MUI Drawer on mobile).
    document.addEventListener('scroll', handleScrollOrResize, { capture: true, passive: true })
    window.visualViewport?.addEventListener('scroll', handleScrollOrResize, { passive: true })
    window.visualViewport?.addEventListener('resize', handleScrollOrResize, { passive: true })

    return () => {
      mentionObserver.disconnect()
      document.removeEventListener('scroll', handleScrollOrResize, { capture: true })
      window.visualViewport?.removeEventListener('scroll', handleScrollOrResize)
      window.visualViewport?.removeEventListener('resize', handleScrollOrResize)
      editor.el?.removeEventListener('tribute-active-true', resetFlipState)
      if (scrollRafId !== null) cancelAnimationFrame(scrollRafId)

      if (tributeRef.current && editor.el) {
        tributeRef.current.detach(editor.el)
        tributeRef.current = null
      }
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorInstance, tenantMemberId, isEnabled, debouncedSearchMembers])
}
